import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { KRAFT } from '@/constants/testIds';
import { scrollToId } from '@/components/SmoothScroll';

/**
 * Kinetic hero with masked line-by-line reveal (Framer).
 * Uses real photo (piorun uderzający w konstrukcję) as background with vignette + subtle parallax.
 */
export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-30%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const H1_LINES = ['Instalacje,', 'które dają', 'święty spokój.'];

  return (
    <section
      id="top"
      ref={ref}
      data-testid={KRAFT.heroRoot}
      className="relative min-h-[100svh] w-full overflow-hidden bg-ink-950"
    >
      {/* Parallax photo background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 hero-photo will-change-transform"
      />
      {/* Vignette + tint */}
      <div className="absolute inset-0 hero-vignette" />
      {/* Volt scanline accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="voltage-line absolute top-[22%] left-0 right-0" />
        <div className="voltage-line absolute bottom-[18%] left-0 right-0 opacity-30" />
      </div>
      {/* Grid overlay */}
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
      {/* Grain */}
      <div className="grain-overlay" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-40 pb-24 min-h-[100svh] flex flex-col justify-between"
      >
        {/* Top row — 24/7 emergency badge (right) */}
        <div className="flex items-start justify-end gap-6">
          <motion.button
            data-testid={KRAFT.heroBadge247}
            onClick={() => scrollToId('booking')}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="hidden md:flex items-center gap-3 px-4 py-2.5 border border-volt bg-ink-950/70 backdrop-blur-md animate-pulse-glow"
          >
            <span className="relative flex w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-red-500 animate-ping" />
              <span className="relative w-2.5 h-2.5 rounded-full bg-red-500" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-white">
              Pogotowie Elektryczne · <span className="text-volt font-bold">24/7</span>
            </span>
          </motion.button>
        </div>

        {/* Center — masked kinetic H1 */}
        <div className="flex-1 flex items-center py-16">
          <div className="w-full">
            <h1 className="display-xxl text-white text-[13vw] md:text-[9vw] lg:text-[8.2vw] leading-[0.88]">
              {H1_LINES.map((line, i) => (
                <span key={i} className="mask-line">
                  <motion.span
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.25 + i * 0.16 }}
                    className="block"
                  >
                    {i === 2 ? (
                      <>
                        <span className="italic font-normal text-volt" style={{ fontStyle: 'italic' }}>święty spokój.</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.6 }}
              className="mt-8 max-w-2xl text-slate-300 text-base md:text-lg leading-relaxed"
            >
              Zero ryzyka, zero niedomówień. Pracujemy w oparciu o <span className="text-white font-semibold">przepisy</span> i <span className="text-white font-semibold">certyfikaty SEP</span>. Kiedy my kończymy zlecenie, Ty zyskujesz <span className="text-white font-semibold">100% pewności</span>, bezpieczeństwa i pełną ochronę prawną.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid={KRAFT.heroCtaPrimary}
                onClick={() => scrollToId('booking')}
                className="btn-volt"
              >
                Zarezerwuj termin
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>

        {/* Bottom row — signature meta bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-2 border-t border-white/10 pt-6"
        >
          <div className="flex gap-10">
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">Warszawa</div>
              <div className="font-display uppercase text-white text-lg tracking-wide">Wał Miedzeszyński 466U</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">Reakcja</div>
              <div className="font-display uppercase text-white text-lg tracking-wide">&lt; 60 min</div>
            </div>
          </div>
          <button
            data-testid={KRAFT.heroScrollHint}
            onClick={() => scrollToId('marquee')}
            className="group flex items-center gap-3 text-slate-300 hover:text-white transition-colors"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.24em]">Przewiń niżej</span>
            <span className="w-10 h-10 border border-white/20 group-hover:border-volt flex items-center justify-center transition-colors">
              <ArrowDown className="w-4 h-4 group-hover:text-volt transition-colors animate-bounce" />
            </span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
